/**
 * Multi-Select Component
 * Allows selection of multiple items from a dropdown list
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-4
 *
 * @description
 * Multi-select component using shadcn Popover and Checkbox pattern:
 * - Multiple selection support
 * - Select/Deselect all functionality
 * - Badge display for selected items
 * - Keyboard navigation support
 * - Accessible design
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   value={['option1']}
 *   onChange={(values) => console.log(values)}
 *   placeholder="Select options"
 * />
 * ```
 */

import React, { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  /**
   * Available options to select from
   */
  options: MultiSelectOption[];

  /**
   * Currently selected values
   */
  value: string[];

  /**
   * Callback when selection changes
   */
  onChange: (value: string[]) => void;

  /**
   * Placeholder text
   * @default "Select items..."
   */
  placeholder?: string;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
}

/**
 * Multi-Select Component
 */
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select items...',
  className,
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);

  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleSelectAll = () => {
    onChange(options.map((opt) => opt.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleRemoveItem = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  const selectedOptions = options.filter((opt) => value.includes(opt.value));
  const isAllSelected = value.length === options.length && options.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between min-h-10 h-auto',
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="mr-1"
                >
                  {option.label}
                  <button
                    type="button"
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveItem(option.value);
                    }}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <div className="max-h-64 overflow-auto">
          {options.length > 0 && (
            <div className="flex items-center justify-between px-2 py-1.5 border-b">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                disabled={isAllSelected}
                className="h-8 px-2"
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                disabled={value.length === 0}
                className="h-8 px-2"
              >
                Clear All
              </Button>
            </div>
          )}
          {options.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              No options available
            </div>
          ) : (
            <div className="p-1">
              {options.map((option) => {
                const isSelected = value.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 rounded-sm px-2 py-1.5 cursor-pointer hover:bg-accent"
                    onClick={() => handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(option.value)}
                      aria-label={`Select ${option.label}`}
                    />
                    <label
                      className="flex-1 cursor-pointer text-sm"
                      onClick={(e) => e.preventDefault()}
                    >
                      {option.label}
                    </label>
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
